// VIB-724b: Hooks og helpers for å synke Monday fase-status (Pre / Prod / Post)
// til Sølvrev task_status. Edge function `monday-phase-status` henter status
// per Monday-prosjekt, vi mapper teksten til vårt status-vokabular og bruker
// forward-only-policy for å unngå nedgradering.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  supabase,
  SUPABASE_FUNCTIONS_URL,
  SUPABASE_ANON_KEY,
} from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

export type MondayStatus = "not_started" | "in_progress" | "approved";

/** Phase-keys må matche `budget_lines.phase` i DB. */
export type PhaseKey = "preprod" | "production" | "postprod";

export interface PhaseStatusSnapshot {
  preprod: string | null;
  production: string | null;
  postprod: string | null;
}

export type SnapshotResponse = Record<string, PhaseStatusSnapshot>;

/**
 * Rangering for forward-only-policy. Verdier hentet fra spec:
 * - rejected = -1 (negativt, så det aldri vinner mot annet enn ukjent < -1)
 * - not_started = 0
 * - in_progress = 1
 * - completed = 2
 * - approved = 3
 *
 * Mapping ned til Monday gir bare not_started / in_progress / approved
 * (vi har ikke kilde i Monday for "completed"). Vi tolker "Ferdig" i Monday
 * som "godkjent av PL", altså `approved` i Sølvrev.
 */
export const STATUS_RANK: Record<string, number> = {
  rejected: -1,
  not_started: 0,
  in_progress: 1,
  completed: 2,
  approved: 3,
};

/**
 * Mapper en Monday-status-tekst til vårt internt status-vokabular.
 * Case-insensitive. Ukjente verdier → `not_started` (safe default).
 *
 * VIKTIG: "ferdig"/"godkjent"/"bekreftet" mapper alle til `approved` fordi
 * Monday ikke skiller mellom "ferdig hos utfører" og "godkjent av leder" —
 * når PL setter status til Ferdig i Monday er det fordi de allerede har
 * godkjent. Forward-only-policy beskytter mot at "Pågår" overskriver
 * eksisterende approved/completed i Sølvrev.
 */
export function mapMondayStatus(label: string | null): MondayStatus {
  const lower = (label ?? "").toLowerCase().trim();
  if (lower === "" || lower === "ikke startet") return "not_started";
  if (lower === "pågår" || lower === "igang") return "in_progress";
  if (lower === "ferdig" || lower === "godkjent" || lower === "bekreftet") {
    return "approved";
  }
  return "not_started";
}

/**
 * Forward-only: returnerer true bare hvis `next` har høyere rank enn `current`.
 * Brukes for å filtrere bort rader hvor synking ville nedgradere status.
 */
export function shouldUpdate(current: string, next: MondayStatus): boolean {
  const currentRank = STATUS_RANK[current] ?? 0;
  const nextRank = STATUS_RANK[next] ?? 0;
  return nextRank > currentRank;
}

/**
 * Hent fase-status-snapshot fra Monday for et sett project_codes.
 * Edge function gjør Supabase-oppslag → Monday GraphQL → kombinerer.
 */
export function useMondayPhaseSnapshot(projectCodes: string[], enabled = false) {
  const sortedCodes = [...projectCodes].sort();
  return useQuery<SnapshotResponse>({
    queryKey: ["monday-phase-snapshot", sortedCodes.join(",")],
    enabled: enabled && projectCodes.length > 0,
    queryFn: async () => {
      // Bruk innlogget brukers JWT — edge function krever admin-token.
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token ?? SUPABASE_ANON_KEY;

      const url = new URL(`${SUPABASE_FUNCTIONS_URL}/monday-phase-status`);
      url.searchParams.set("project_codes", projectCodes.join(","));

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY,
        },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`monday-phase-status (${res.status}): ${text.slice(0, 300)}`);
      }
      return (await res.json()) as SnapshotResponse;
    },
    staleTime: 60 * 1000,
  });
}

export interface ApplyPhaseStatusUpdate {
  assignmentId: string;
  newStatus: MondayStatus;
}

/**
 * Batch-oppdater `task_status` på `budget_line_assignments` i Supabase.
 * Caller har allerede filtrert bort rader hvor shouldUpdate=false.
 * Invaliderer my-tasks + project-detail bredt — synken treffer typisk
 * mange prosjekter på én gang.
 */
export function useApplyMondayPhaseStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: ApplyPhaseStatusUpdate[]) => {
      if (updates.length === 0) return { updated: 0 };
      let updated = 0;
      const errors: string[] = [];

      // Vi grupperer på newStatus så vi får færre roundtrips når mange
      // assignments får samme nye status (typisk: alle Pre-oppgaver → approved).
      const byStatus = new Map<MondayStatus, string[]>();
      for (const u of updates) {
        const arr = byStatus.get(u.newStatus) ?? [];
        arr.push(u.assignmentId);
        byStatus.set(u.newStatus, arr);
      }

      for (const [status, ids] of byStatus.entries()) {
        const patch: Record<string, unknown> = { task_status: status };
        // `completed_at` settes når oppgaven blir approved (eller completed)
        // første gang. Hvis allerede satt i DB blir det ikke overskrevet
        // pga forward-only filtreringen — vi treffer kun rader som nedgrader
        // _ikke_ skjer på, så det er trygt å sette completed_at her.
        if (status === "approved") {
          patch.completed_at = new Date().toISOString();
        }
        const { error } = await supabase
          .from("budget_line_assignments")
          .update(patch)
          .in("id", ids);
        if (error) {
          errors.push(`update ${status}: ${error.message}`);
        } else {
          updated += ids.length;
        }
      }

      if (errors.length > 0) {
        throw new Error(errors.join("; "));
      }

      return { updated };
    },
    onSuccess: async (result) => {
      // Bred invalidering: én sync treffer typisk flere prosjekter (admin
      // valgte multi-prosjekter), så vi kan ikke målrette mot én project_id
      // eller user_id (VIB-649). invalidateFor uten kontekst faller tilbake
      // til prefix-invalidering for alle registry-keys for tabellen.
      await invalidateFor(qc, "budget_line_assignments");
      toast.success(`Oppdaterte ${result.updated} oppgaver`);
    },
    onError: (err) => {
      toast.error(`Sync feilet: ${(err as Error).message}`);
    },
  });
}
